'use client';

import {useBEApiClient} from '@hooks/network/useApiClient';
import type {PropsWithChildren} from 'react';
import {useEffect, useRef} from 'react';
import useSWRImmutable from 'swr/immutable';

import {IntegrationManifestsContext, createIntegrationManifestsStore} from './store';

export const IntegrationManifestsProvider = (props: PropsWithChildren) => {
  const {children} = props;
  const api = useBEApiClient();
  const {data: {data: regexMap} = {}} = useSWRImmutable(`manifests-regexp`, () => api?.publicSystemNameRegex(), {
    errorRetryCount: 10,
    keepPreviousData: true,
    revalidateOnReconnect: true,
  });
  const {data: {data: manifests} = {}, isLoading: isManifestsLoading} = useSWRImmutable(
    `integrations-manifests`,
    () => api?.publicListIntegrationsManifests({limit: 400}),
    {
      errorRetryCount: 10,
      keepPreviousData: true,
      revalidateOnReconnect: true,
    },
  );

  // TODO: Remove after 2026-09-01 — cleans up stale persisted manifests from localStorage
  useEffect(() => {
    try {
      localStorage.removeItem('linx@IntegrationManifestsStore@1');
    } catch {
      console.error('Failed to remove linx@IntegrationManifestsStore@1 from localStorage');
    }
  }, []);

  const storeRef = useRef<ReturnType<typeof createIntegrationManifestsStore> | null>(createIntegrationManifestsStore());

  useEffect(() => {
    if (regexMap) {
      storeRef.current?.getState().setRegexMap(regexMap);
    }

    if (manifests && !isManifestsLoading) {
      storeRef.current?.getState().setManifests(manifests.items);
    }
  }, [regexMap, manifests, isManifestsLoading]);

  useEffect(() => {
    storeRef.current?.getState().setIsManifestsLoading(isManifestsLoading);
  }, [isManifestsLoading]);

  return (
    <IntegrationManifestsContext.Provider value={storeRef.current}>{children}</IntegrationManifestsContext.Provider>
  );
};
